import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useColorContext } from '../../../context/colorContext';

const toSafeHex = (hex, fallback) => {
  const value = String(hex || '').trim();
  return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback) => {
  const safeHex = toSafeHex(hex, fallback);
  const r = parseInt(safeHex.slice(1, 3), 16);
  const g = parseInt(safeHex.slice(3, 5), 16);
  const b = parseInt(safeHex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export default function TermsOfService() {
  const { bgPrimary, bgSecondary, textPrimary, textSecondary, activeAccent, varkColors } = useColorContext();

  const safeBgPrimary = toSafeHex(bgPrimary, '#0A0A0F');
  const safeBgSecondary = toSafeHex(bgSecondary, '#13131A');
  const safeTextPrimary = toSafeHex(textPrimary, '#FFFFFF');
  const safeTextSecondary = toSafeHex(textSecondary, '#A1A1AA');
  const safeAccent = toSafeHex(activeAccent, '#6366F1');
  const visualColor = toSafeHex(varkColors?.visual, '#7C3AED');
  const auditoryColor = toSafeHex(varkColors?.auditory, '#3B82F6');

  const containerStyle = {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${safeBgPrimary} 0%, ${hexToRgba(visualColor, 0.05, safeBgPrimary)} 20%, ${hexToRgba(auditoryColor, 0.05, safeBgPrimary)} 45%, ${hexToRgba(safeAccent, 0.06, safeBgPrimary)} 70%, ${safeBgPrimary} 100%)`,
    color: safeTextPrimary,
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    padding: '160px 24px 64px',
  };

  const contentStyle = {
    maxWidth: 900,
    margin: '0 auto',
  };

  const titleStyle = {
    fontSize: 'clamp(2.5rem, 4vw, 3.5rem)',
    fontWeight: 800,
    marginBottom: 12,
    background: `linear-gradient(135deg, ${safeTextPrimary} 0%, ${hexToRgba(safeAccent, 0.9, safeAccent)} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  };

  const lastUpdatedStyle = {
    fontSize: '0.95rem',
    color: safeTextSecondary,
    marginBottom: 48,
    fontStyle: 'italic',
  };

  const headingStyle = (level) => ({
    fontSize: level === 2 ? '1.8rem' : '1.4rem',
    fontWeight: level === 2 ? 700 : 600,
    marginTop: level === 2 ? 40 : 28,
    marginBottom: 16,
    color: safeTextPrimary,
  });

  const textStyle = {
    fontSize: '1rem',
    lineHeight: 1.7,
    color: safeTextSecondary,
    marginBottom: 16,
  };

  const ulStyle = {
    listStyle: 'disc',
    paddingLeft: 24,
    marginBottom: 16,
    color: safeTextSecondary,
  };

  const liStyle = {
    marginBottom: 8,
    lineHeight: 1.6,
  };

  const boldTextStyle = {
    color: safeTextPrimary,
    fontWeight: 600,
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: 24,
    border: `1px solid ${hexToRgba(safeAccent, 0.2, safeAccent)}`,
    borderRadius: 12,
    overflow: 'hidden',
  };

  const thStyle = {
    background: hexToRgba(safeBgSecondary, 0.8, safeBgSecondary),
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: 600,
    borderBottom: `1px solid ${hexToRgba(safeAccent, 0.2, safeAccent)}`,
    color: safeTextPrimary,
  };

  const tdStyle = {
    padding: '12px 16px',
    borderBottom: `1px solid ${hexToRgba(safeAccent, 0.1, safeAccent)}`,
    color: safeTextSecondary,
  };

  const contactStyle = {
    background: hexToRgba(safeBgSecondary, 0.6, safeBgSecondary),
    padding: '20px 24px',
    borderRadius: 12,
    border: `1px solid ${hexToRgba(safeAccent, 0.2, safeAccent)}`,
    marginTop: 20,
    color: safeTextSecondary,
  };

  return (
    <div style={containerStyle}>
      <Helmet>
        <title>Terms of Service — Plynote</title>
        <meta name="description" content="Read Plynote's terms of service and usage conditions for our AI-powered scientific document tool." />
        <link rel="canonical" href="https://www.plynote.io/terms-of-service" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,300;14..32,400;14..32,500;14..32,600;14..32,700;14..32,800&display=swap');

          body {
            font-family: 'Inter', sans-serif;
          }

          ::-webkit-scrollbar {
            width: 8px;
          }

          ::-webkit-scrollbar-track {
            background: ${hexToRgba(safeBgSecondary, 0.5, safeBgSecondary)};
          }

          ::-webkit-scrollbar-thumb {
            background: ${hexToRgba(safeAccent, 0.5, safeAccent)};
            border-radius: 4px;
          }

          ::-webkit-scrollbar-thumb:hover {
            background: ${hexToRgba(safeAccent, 0.7, safeAccent)};
          }
        `}
      </style>

      <div style={contentStyle}>
        <h1 style={titleStyle}>Terms of Service</h1>
        <div style={lastUpdatedStyle}>Last updated: April 28, 2026</div>

        <section>
          <h2 style={headingStyle(2)}>1. Acceptance of Terms</h2>
          <p style={textStyle}>
            By accessing or using Plynote, you agree to be bound by these Terms of Service. If you disagree with any part, do not use the Service.
          </p>
        </section>

        <section>
          <h2 style={headingStyle(2)}>2. Description of Service</h2>
          <p style={textStyle}>Plynote is an AI-powered note-taking tool that:</p>
          <ul style={ulStyle}>
            <li style={liStyle}>Extracts content from PDFs, audio files, YouTube links, Word documents, and Markdown files</li>
            <li style={liStyle}>Provides an editor for annotations and AI chat assistance</li>
            <li style={liStyle}>Generates learning formats: mindmaps, flashcards, audio summaries, and smart text (VARK adaptation)</li>
          </ul>
        </section>

        <section>
          <h2 style={headingStyle(2)}>3. Account Registration</h2>
          <ul style={ulStyle}>
            <li style={liStyle}>You must provide a valid email address</li>
            <li style={liStyle}>You are responsible for maintaining account security</li>
            <li style={liStyle}>You are responsible for all activity under your account</li>
            <li style={liStyle}>You must be at least 13 years old to use the Service</li>
          </ul>
        </section>

        <section>
          <h2 style={headingStyle(2)}>4. Subscriptions and Payments</h2>
          
          <h3 style={headingStyle(3)}>4.1 Plans</h3>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Plan</th>
                <th style={thStyle}>Price</th>
                <th style={thStyle}>Features</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyle}><span style={boldTextStyle}>Free</span></td>
                <td style={tdStyle}>$0</td>
                <td style={tdStyle}>3 formats adaptation, 5 uploads, 10 chat messages</td>
              </tr>
              <tr>
                <td style={tdStyle}><span style={boldTextStyle}>Basic</span></td>
                <td style={tdStyle}>$12.99/month</td>
                <td style={tdStyle}>40 formats adaptation/month, unlimited chat conversations, unlimited uploads</td>
              </tr>
              <tr>
                <td style={tdStyle}><span style={boldTextStyle}>Pro</span></td>
                <td style={tdStyle}>$19.99/month</td>
                <td style={tdStyle}>120 formats adaptation/month, unlimited chat conversations, unlimited uploads</td>
              </tr>
              <tr>
                <td style={tdStyle}><span style={boldTextStyle}>Expert</span></td>
                <td style={tdStyle}>$29.99/month</td>
                <td style={tdStyle}>Unlimited formats, priority support, advanced exports</td>
              </tr>
            </tbody>
          </table>

          <h3 style={headingStyle(3)}>4.2 Billing</h3>
          <ul style={ulStyle}>
            <li style={liStyle}>Subscriptions automatically renew monthly or yearly (depending on your selection)</li>
            <li style={liStyle}>Cancellation takes effect at the end of the current billing period</li>
            <li style={liStyle}>No refunds for partial months (but you keep access until the period ends)</li>
          </ul>

          <h3 style={headingStyle(3)}>4.3 Payment Processing</h3>
          <ul style={ulStyle}>
            <li style={liStyle}>All payments are processed securely by Stripe</li>
            <li style={liStyle}>We do not store your payment information</li>
          </ul>
        </section>

        <section>
          <h2 style={headingStyle(2)}>5. Free Trial</h2>
          <p style={textStyle}>
            The Free plan is a permanent free trial: 3 formats adaptation, 5 uploads, 10 chat messages, no credit card required. You can upgrade or cancel anytime.
          </p>
        </section>

        <section>
          <h2 style={headingStyle(2)}>6. Cancellation and Termination</h2>
          
          <h3 style={headingStyle(3)}>6.1 By you</h3>
          <ul style={ulStyle}>
            <li style={liStyle}>Cancel anytime from your dashboard</li>
            <li style={liStyle}>No cancellation fees</li>
            <li style={liStyle}>Your data remains available until the end of your billing period</li>
          </ul>

          <h3 style={headingStyle(3)}>6.2 By us</h3>
          <p style={textStyle}>We may suspend or terminate your account if:</p>
          <ul style={ulStyle}>
            <li style={liStyle}>You violate these Terms</li>
            <li style={liStyle}>Your payment fails repeatedly</li>
            <li style={liStyle}>You use the Service for illegal purposes</li>
          </ul>
        </section>

        <section>
          <h2 style={headingStyle(2)}>7. Your Content</h2>
          
          <h3 style={headingStyle(3)}>7.1 Ownership</h3>
          <ul style={ulStyle}>
            <li style={liStyle}>You retain all ownership rights to your documents, notes, and generated content</li>
            <li style={liStyle}>Plynote claims no ownership over your content</li>
          </ul>

          <h3 style={headingStyle(3)}>7.2 License to us</h3>
          <p style={textStyle}>By using the Service, you grant us a limited license to:</p>
          <ul style={ulStyle}>
            <li style={liStyle}>Process your documents to provide the Service (extraction, AI generation, formatting)</li>
            <li style={liStyle}>Store your content on secure servers</li>
            <li style={liStyle}>This license ends when you delete your content or close your account</li>
          </ul>

          <h3 style={headingStyle(3)}>7.3 Prohibited content</h3>
          <p style={textStyle}>You may not upload:</p>
          <ul style={ulStyle}>
            <li style={liStyle}>Illegal content</li>
            <li style={liStyle}>Malicious code or viruses</li>
            <li style={liStyle}>Content that infringes third-party rights</li>
          </ul>
        </section>

        <section>
          <h2 style={headingStyle(2)}>8. AI-Generated Content</h2>
          <ul style={ulStyle}>
            <li style={liStyle}>AI-generated content may contain errors</li>
            <li style={liStyle}>You are responsible for verifying critical information</li>
            <li style={liStyle}>We do not guarantee accuracy for medical, legal, or financial decisions</li>
          </ul>
        </section>

        <section>
          <h2 style={headingStyle(2)}>9. Intellectual Property</h2>
          <ul style={ulStyle}>
            <li style={liStyle}>The Plynote software, design, and branding are our intellectual property</li>
            <li style={liStyle}>You may not copy, modify, or reverse-engineer the Service</li>
          </ul>
        </section>

        <section>
          <h2 style={headingStyle(2)}>10. Limitation of Liability</h2>
          <p style={textStyle}>To the maximum extent permitted by law:</p>
          
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Exclusion</th>
                <th style={thStyle}>Details</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyle}><span style={boldTextStyle}>No warranties</span></td>
                <td style={tdStyle}>The Service is provided "as is" without warranties</td>
              </tr>
              <tr>
                <td style={tdStyle}><span style={boldTextStyle}>No liability for data loss</span></td>
                <td style={tdStyle}>Keep backups of important documents</td>
              </tr>
              <tr>
                <td style={tdStyle}><span style={boldTextStyle}>No liability for errors</span></td>
                <td style={tdStyle}>AI extraction may produce errors</td>
              </tr>
              <tr>
                <td style={tdStyle}><span style={boldTextStyle}>Maximum liability</span></td>
                <td style={tdStyle}>Limited to the amount you paid in the last 3 months</td>
              </tr>
            </tbody>
          </table>

          <p style={textStyle}>
            <span style={boldTextStyle}>Example:</span> If you paid $12.99 for Basic, our maximum liability is $12.99.
          </p>
        </section>

        <section>
          <h2 style={headingStyle(2)}>11. Indemnification</h2>
          <p style={textStyle}>You agree to indemnify and hold harmless Plynote from any claims arising from:</p>
          <ul style={ulStyle}>
            <li style={liStyle}>Your use of the Service</li>
            <li style={liStyle}>Your violation of these Terms</li>
            <li style={liStyle}>Your violation of third-party rights</li>
          </ul>
        </section>

        <section>
          <h2 style={headingStyle(2)}>12. Changes to the Service</h2>
          <p style={textStyle}>We may:</p>
          <ul style={ulStyle}>
            <li style={liStyle}>Add or remove features at any time</li>
            <li style={liStyle}>Update pricing (with 30 days notice for existing subscribers)</li>
            <li style={liStyle}>Discontinue the Service (with 30 days notice and pro-rata refunds)</li>
          </ul>
        </section>

        <section>
          <h2 style={headingStyle(2)}>13. Governing Law</h2>
          <p style={textStyle}>These Terms are governed by:</p>
          
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>If your billing address is in</th>
                <th style={thStyle}>Governing law</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyle}>Europe</td>
                <td style={tdStyle}>French law</td>
              </tr>
              <tr>
                <td style={tdStyle}>United States</td>
                <td style={tdStyle}>California law</td>
              </tr>
              <tr>
                <td style={tdStyle}>Other</td>
                <td style={tdStyle}>French law</td>
              </tr>
            </tbody>
          </table>

          <p style={textStyle}>
            <span style={boldTextStyle}>Disputes will be resolved through:</span>
          </p>
          <ul style={ulStyle}>
            <li style={liStyle}>Amicable negotiation first</li>
            <li style={liStyle}>Mediation (free for users)</li>
            <li style={liStyle}>Court of competent jurisdiction</li>
          </ul>
        </section>

        <section>
          <h2 style={headingStyle(2)}>14. Data Processing</h2>
          <p style={textStyle}>
            We process your data as described in our Privacy Policy.
          </p>
          <p style={textStyle}>
            For GDPR (Europe) or CCPA (California) requests: <span style={boldTextStyle}>plynote.sender@gmail.com</span>
          </p>
        </section>

        <section>
          <h2 style={headingStyle(2)}>15. Contact</h2>
          <p style={textStyle}>
            For legal or Terms-related questions:
          </p>
          <div style={contactStyle}>
            <p style={{ marginBottom: 8 }}>📧 <span style={boldTextStyle}>plynote.sender@gmail.com</span></p>
            <p style={{ marginBottom: 8 }}>@Plynote</p>
          </div>
        </section>

        <section>
          <h2 style={headingStyle(2)}>16. Entire Agreement</h2>
          <p style={textStyle}>
            These Terms constitute the entire agreement between you and Plynote regarding the Service.
          </p>
        </section>

        <section style={{ marginTop: 60, paddingTop: 40, borderTop: `1px solid ${hexToRgba(safeAccent, 0.2, safeAccent)}` }}>
          <Link to="/privacy-policy" style={{ textDecoration: 'none', color: 'inherit' }}>
            <p style={{ ...textStyle, color: safeAccent, fontSize: '1.1rem', fontWeight: 600, cursor: 'pointer' }}>
              ← Read our Privacy Policy
            </p>
          </Link>
        </section>
      </div>
    </div>
  );
}
